import { useCallback, useMemo } from "react"
import {
  Box,
  Button,
  CardMedia,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { useParams } from "react-router-dom"
import { ContentWithAside } from "layouts/kiosk-h"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"

import { QRCode } from "components/QRCode"
import { PageCard, PageCardContent } from "components/card"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"

import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { usePostDetail } from "hooks/post/usePostDetail"
import { useMobileUrl } from "hooks/useMobileUrl"
import { EmptyData } from "components/EmptyData"
import { getIcon } from "utils/icon"
import { ServiceIcon } from "components/icons"

export const LuxuryDetailKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { goBack } = useAppUI()
  const { t } = useTranslation()
  const theme = useTheme()
  const { luxuryId } = useParams()
  const { post, navigateToLocation } = usePostDetail(luxuryId)
  const { mobileUrl } = useMobileUrl({ path: post?.locationUrl })
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.luxury) || <ServiceIcon />,
    [theme]
  )

  const styleOverrides =
    theme.components[`LuxuryDetailPage`]?.styleOverrides || {}
  const {
    pageCard,
    pageCardContent,
    contentWrapper,
    gallaryWrapper,
    mainBox,
    button,
    buttonContainer,
    logoWrapper,
    qrCodeContainer,
    qrCode,
    qrCodeCaptionContainer,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    emptyDataWrapper,
    cardMediaStyle,
  } = styleOverrides

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          {post?.id ? (
            <Box sx={contentWrapper}>
              {post?.gallery && post?.gallery.length > 0 && (
                <>
                  <Box sx={gallaryWrapper}>
                    {(post?.gallery || []).slice(0, 4).map((item, idx) => {
                      return (
                        <CardMedia
                          component="img"
                          src={item}
                          sx={cardMediaStyle}
                          key={idx}
                        />
                      )
                    })}
                  </Box>
                </>
              )}

              <Box
                sx={{
                  ...mainBox,
                  backgroundImage: `url(${post?.coverImage})`,
                }}
              >
                {/* ----- Logo ----- */}
                <Box sx={logoWrapper}>
                  <CardMedia
                    component="img"
                    src={post?.locationLinkInfo?.image}
                  />
                </Box>

                {/* ----- Action ----- */}
                <Box key="app-navigation" sx={buttonContainer}>
                  <Box sx={qrCodeContainer}>
                    <QRCode sx={qrCode} url={mobileUrl} />
                    <Box sx={qrCodeCaptionContainer}>
                      <Typography
                        variant="subtitle2"
                        component="p"
                        sx={qrcodePrimaryText}
                      >
                        {t("Scan Here")}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="p"
                        sx={qrcodeSecondaryText}
                      >
                        {t("For Mobile")}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1.25}>
                    <Button
                      variant="outlined"
                      sx={{
                        ...button,
                        backgroundColor: "transparent",
                        color: "common.white",
                      }}
                      onClick={handleClickBack}
                    >
                      {t("Back")}
                    </Button>
                    {post?.hasLocation && (
                      <Button
                        variant="contained"
                        sx={button}
                        className="highlight"
                        onClick={navigateToLocation}
                      >
                        {t("View Location")}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t(`No Current Luxury`)}
                secondary={t("Check Back Soon")}
              />
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
